import React, { Component } from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import FoundationLayout from '../components/layout/FoundationLayout';
import FoundationMultiSelect from '../components/common/FoundationMultiSelect';
import { getFoundationStudent } from '../foundationSession';
import './_foundationErrorExams.scss';

/**
 * IIT Foundation — Error Exams builder.
 *
 * The Subjects and Chapters lists are the student's OWN error topics: only the
 * subjects/chapters where they have previously answered questions incorrectly
 * (getFoundationErrorTopics). The student picks any number of each (both
 * multi-select), then starts an error exam re-testing those wrong questions.
 */
const GET_FOUNDATION_ERROR_TOPICS = gql`
  query ($registration_id: Int!) {
    getFoundationErrorTopics(registration_id: $registration_id) {
      subject_id
      subject
      chapter_id
      chapter_name
      chapter_number
      error_count
    }
  }
`;

// per-subject accent colour + icon, matched case-insensitively on the name
// (colours follow the foundation theme's subject palette, see theme/README.md)
const SUBJECT_STYLE = [
  { test: /phys/i, color: 'var(--fdn-violet)', icon: 'fa-atom' },
  { test: /chem/i, color: 'var(--fdn-teal)', icon: 'fa-flask' },
  { test: /bio/i, color: 'var(--fdn-green)', icon: 'fa-dna' },
  { test: /math/i, color: 'var(--fdn-orange)', icon: 'fa-square-root-alt' },
  { test: /reason|apti|mental/i, color: 'var(--fdn-yellow)', icon: 'fa-brain' },
  { test: /sci/i, color: 'var(--fdn-pink)', icon: 'fa-microscope' },
  { test: /eng|lang/i, color: 'var(--fdn-blue)', icon: 'fa-book-open' },
];
const styleFor = (subject) => SUBJECT_STYLE.find((x) => x.test.test(subject || '')) || { color: 'var(--fdn-muted)', icon: 'fa-layer-group' };

class ErrorExamsView extends Component {
  state = { subjects: [], chapters: [] }; // selected subject_ids / chapter_ids (numbers)

  // ---- derived option lists (the student's own error topics) -------------
  get allChapters() {
    return this.props.exams && this.props.exams.getFoundationErrorTopics
      ? this.props.exams.getFoundationErrorTopics
      : [];
  }

  get subjectOptions() {
    const byId = {};
    const list = [];
    this.allChapters.forEach((c) => {
      if (c.subject_id == null) return;
      const id = Number(c.subject_id);
      if (byId[id] === undefined) {
        byId[id] = list.length;
        list.push({ id, name: c.subject || 'Other', errors: 0 });
      }
      list[byId[id]].errors += Number(c.error_count) || 0;
    });
    return list;
  }

  // chapters shown depend on which subjects are picked (none = all)
  get visibleChapters() {
    const { subjects } = this.state;
    if (!subjects.length) return this.allChapters;
    return this.allChapters.filter((c) => subjects.indexOf(Number(c.subject_id)) !== -1);
  }

  // ---- selection toggles -------------------------------------------------
  toggle = (key, id) => {
    id = Number(id);
    this.setState((s) => {
      const cur = s[key];
      const next = cur.indexOf(id) === -1 ? cur.concat(id) : cur.filter((x) => x !== id);
      const patch = { [key]: next };
      // if a subject was removed, drop chapters that no longer belong to any picked subject
      if (key === 'subjects') {
        const allowed = next.length
          ? this.allChapters.filter((c) => next.indexOf(Number(c.subject_id)) !== -1).map((c) => Number(c.chapter_id))
          : null;
        if (allowed) patch.chapters = s.chapters.filter((cid) => allowed.indexOf(cid) !== -1);
      }
      return patch;
    });
  };

  allSubjectsSelected() {
    const opts = this.subjectOptions;
    return opts.length > 0 && opts.every((o) => this.state.subjects.indexOf(o.id) !== -1);
  }
  toggleAllSubjects = () => {
    if (this.allSubjectsSelected()) this.setState({ subjects: [], chapters: [] });
    else this.setState({ subjects: this.subjectOptions.map((o) => o.id) });
  };

  allChaptersSelected() {
    const opts = this.visibleChapters;
    return opts.length > 0 && opts.every((o) => this.state.chapters.indexOf(Number(o.chapter_id)) !== -1);
  }
  toggleAllChapters = () => {
    if (this.allChaptersSelected()) this.setState({ chapters: [] });
    else this.setState({ chapters: this.visibleChapters.map((o) => Number(o.chapter_id)) });
  };

  // ---- dropdown change handlers -----------------------------------------
  onSubjectsChange = (next) => {
    this.setState((s) => {
      const patch = { subjects: next };
      const allowed = next.length
        ? this.allChapters.filter((c) => next.indexOf(Number(c.subject_id)) !== -1).map((c) => Number(c.chapter_id))
        : null;
      if (allowed) patch.chapters = s.chapters.filter((cid) => allowed.indexOf(cid) !== -1);
      return patch;
    });
  };
  onChaptersChange = (next) => this.setState({ chapters: next });

  // ---- start -------------------------------------------------------------
  start = () => {
    const { subjects, chapters } = this.state;
    const qs = new URLSearchParams({ mode: 'error' });
    if (subjects.length) qs.set('subjects', subjects.join(','));
    if (chapters.length) qs.set('chapters', chapters.join(','));
    const url = `/iit-foundation/take-test?${qs.toString()}`;
    if (this.props.history) this.props.history.push(url);
    else window.location.assign(url);
  };

  render() {
    const student = getFoundationStudent() || {};
    const loading = this.props.exams && this.props.exams.loading;
    const subjectOptions = this.subjectOptions;
    const chapters = this.visibleChapters;
    const { subjects: selSub, chapters: selCh } = this.state;

    return (
      <FoundationLayout student={getFoundationStudent()} title="Error Exams">
        <div className="fee">
          <div className="fee-head fdn-hero fdn-hero--red">
            <div className="fdn-hero__row">
              <span className="fdn-hero__icon"><i className="fas fa-redo-alt" /></span>
              <div>
                <h2 className="fdn-hero__title">Error Exams</h2>
                <p className="fdn-hero__sub">
                  {student.class ? `${student.class} · ` : ''}
                  Re-test the questions you got wrong. These are the subjects and chapters where you have errors — pick any, or leave a section empty to include all of them.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="fee-empty fee-loading fdn-card">
              <span className="fdn-skel" style={{ width: '30%' }} />
              <div className="fee-selects">
                <span className="fdn-skel fee-skel-field" />
                <span className="fdn-skel fee-skel-field" />
              </div>
              <p><i className="fas fa-circle-notch fa-spin" /> Finding your error questions…</p>
            </div>
          ) : !this.allChapters.length ? (
            <div className="fee-empty fdn-card fdn-empty fee-empty--clean">
              <div className="fdn-empty__icon"><i className="fas fa-star" /></div>
              <h4>A clean slate! ✨</h4>
              <p>
                No error questions yet. Take a few tests first — the questions you get wrong will show up here to re-attempt.
              </p>
            </div>
          ) : (
            <>
              <section className="fee-section fdn-card fdn-pop">
                <div className="fdn-card__head">
                  <span className="fdn-card__icon fee-section-icon"><i className="fas fa-crosshairs" /></span>
                  <div>
                    <h3 className="fdn-card__title">Fix your mistakes 💪</h3>
                    <p className="fdn-card__sub">Numbers show how many questions you got wrong.</p>
                  </div>
                </div>
                <div className="fee-selects">
                  <FoundationMultiSelect
                    label="Subjects"
                    placeholder="All subjects"
                    noun="subjects"
                    options={subjectOptions.map((s) => ({ value: s.id, label: s.name, count: s.errors, color: styleFor(s.name).color }))}
                    selected={selSub}
                    onChange={this.onSubjectsChange}
                  />
                  <FoundationMultiSelect
                    label="Chapters"
                    placeholder="All chapters"
                    noun="chapters"
                    searchable
                    options={chapters.map((c) => ({ value: Number(c.chapter_id), label: c.chapter_name, count: c.error_count, color: styleFor(c.subject).color }))}
                    selected={selCh}
                    onChange={this.onChaptersChange}
                  />
                </div>
              </section>

              {/* Action bar --------------------------------------------- */}
              <div className="fee-bar fdn-card">
                <div className="fee-bar-sum">
                  <span className="fee-bar-pill"><i className="fas fa-book" /> <b>{selSub.length || subjectOptions.length}</b> subject{(selSub.length || subjectOptions.length) === 1 ? '' : 's'}</span>
                  <span className="fee-bar-dot">·</span>
                  <span className="fee-bar-pill"><i className="fas fa-bookmark" /> <b>{selCh.length || chapters.length}</b> chapter{(selCh.length || chapters.length) === 1 ? '' : 's'}</span>
                </div>
                <button type="button" className="fee-start fdn-btn fdn-btn--danger fdn-btn--lg" onClick={this.start}>
                  Start Error Exam <i className="fas fa-arrow-right" />
                </button>
              </div>
            </>
          )}
        </div>
      </FoundationLayout>
    );
  }
}

const ErrorExams = graphql(GET_FOUNDATION_ERROR_TOPICS, {
  name: 'exams',
  skip: () => {
    const s = getFoundationStudent();
    return !(s && s.id);
  },
  options: () => {
    const s = getFoundationStudent();
    return {
      variables: { registration_id: Number(s.id) },
      fetchPolicy: 'cache-and-network',
    };
  },
})(ErrorExamsView);

/** Renders a prompt when there's no session; otherwise the error-exam builder. */
class IITFoundationErrorExams extends Component {
  render() {
    const s = getFoundationStudent();
    if (!s || !s.id) {
      return (
        <FoundationLayout student={s}>
          <div className="fee">
            <div className="fee-empty fdn-card fdn-empty">
              <div className="fdn-empty__icon"><i className="fas fa-user-lock" /></div>
              <h4>Hold on! 🔐</h4>
              <p>Please log in to build an error exam.</p>
            </div>
          </div>
        </FoundationLayout>
      );
    }
    return <ErrorExams {...this.props} />;
  }
}

export default IITFoundationErrorExams;
